import { Component, Inject, HostListener } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as ExcelJS from 'exceljs';
import { UserItemApi, FeedbackItemApi, UserAgentQuestionItemApi, ManagerStatApi } from '../user-dashboard.models';

export interface DetailDialogData {
    type: 'users' | 'feedback' | 'questions' | 'consolidated';
    users?: UserItemApi[];
    feedbackItems?: FeedbackItemApi[];
    allFeedbackItems?: FeedbackItemApi[];
    questionItems?: UserAgentQuestionItemApi[];
    managerStats?: ManagerStatApi[];
}

@Component({
    selector: 'app-detail-dialog',
    templateUrl: './detail-dialog.component.html',
    styleUrls: ['./detail-dialog.component.scss'],
    standalone: false,
})
export class DetailDialogComponent {
    private static readonly USERS_EXPORT_FILENAME_PREFIX = 'Active_Users';
    private static readonly USERS_EXPORT_SHEET_NAME = 'Active Users';
    private static readonly QUESTIONS_EXPORT_FILENAME_PREFIX = 'Questions_Asked';
    private static readonly QUESTIONS_EXPORT_SHEET_NAME = 'Questions Asked';
    private static readonly FEEDBACK_EXPORT_FILENAME_PREFIX = 'Feedback_Comments';
    private static readonly FEEDBACK_EXPORT_SHEET_NAME = 'Feedback';
    private static readonly CONSOLIDATED_EXPORT_FILENAME_PREFIX = 'Consolidated_Report';
    private static readonly CONSOLIDATED_EXPORT_SHEET_NAME = 'Consolidated Report';

    // ── Users view state ────────────────────────────────────────────────────
    public searchTerm = '';
    public currentPage = 0;
    public readonly pageSize = 10;
    public managerDropdownOpen = false;
    public managerDropdownSearch = '';
    public selectedManager: string | null = null;

    // ── Feedback view state ─────────────────────────────────────────────
    public dropdownOpen = false;
    public dropdownSearch = '';
    public selectedUser: string | null = null;
    public ratingDropdownOpen = false;
    public selectedRating: number | null = null; // null means all ratings

    // ── Questions view state ────────────────────────────────────────────
    public questionDropdownOpen = false;
    public questionDropdownSearch = '';
    public selectedQuestionUser: string | null = null;
    public categoryDropdownOpen = false;
    public categoryDropdownSearch = '';
    public selectedCategory: string | null = 'CES Search Agent'; // Default to CES Search Agent

    // ── Consolidated report view state ───────────────────────────────────
    public consolidatedCurrentPage = 0;

    private readonly dateFormatter = new Intl.DateTimeFormat('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: DetailDialogData,
        private dialogRef: MatDialogRef<DetailDialogComponent>,
    ) {}

    // ── Users: computed lists ───────────────────────────────────────────────

    public get filteredUsers(): UserItemApi[] {
        const term = this.searchTerm.trim().toLowerCase();
        const selectedManager = (this.selectedManager || '').trim().toLowerCase();

        return (this.data.users ?? []).filter((u) => {
            const userName = (u.name || '').toLowerCase();
            const managerName = (u.manager_name || '').trim().toLowerCase();

            const matchesSearch = !term || userName.includes(term);
            const matchesManager = !selectedManager || managerName === selectedManager;

            return matchesSearch && matchesManager;
        });
    }

    public get uniqueManagerNames(): string[] {
        const names = (this.data.users ?? [])
            .map((u) => (u.manager_name || '').trim())
            .filter((name) => !!name);
        return [...new Set(names)].sort((a, b) => a.localeCompare(b));
    }

    public get dropdownManagerNames(): string[] {
        const term = this.managerDropdownSearch.trim().toLowerCase();
        if (!term) return this.uniqueManagerNames;
        return this.uniqueManagerNames.filter((name) => name.toLowerCase().includes(term));
    }

    public get pagedUsers(): UserItemApi[] {
        const start = this.currentPage * this.pageSize;
        return this.filteredUsers.slice(start, start + this.pageSize);
    }

    public get totalCount(): number { return this.filteredUsers.length; }
    public get totalPages(): number { return Math.max(1, Math.ceil(this.totalCount / this.pageSize)); }
    public get rangeStart(): number { return this.totalCount === 0 ? 0 : this.currentPage * this.pageSize + 1; }
    public get rangeEnd(): number { return Math.min((this.currentPage + 1) * this.pageSize, this.totalCount); }

    public onSearchChange(): void { this.currentPage = 0; }
    public toggleManagerDropdown(event: MouseEvent): void {
        event.stopPropagation();
        this.managerDropdownOpen = !this.managerDropdownOpen;
        if (this.managerDropdownOpen) {
            this.managerDropdownSearch = '';
        }
    }
    public selectManager(name: string | null, event: MouseEvent): void {
        event.stopPropagation();
        this.selectedManager = name;
        this.managerDropdownOpen = false;
        this.currentPage = 0;
    }
    public prevPage(): void { if (this.currentPage > 0) this.currentPage--; }
    public nextPage(): void { if (this.currentPage < this.totalPages - 1) this.currentPage++; }

    public async downloadUsersAsExcel(): Promise<void> {
        if (this.data.type !== 'users') {
            return;
        }

        try {
            await this.downloadUsersAsExcelInternal();
        } catch (err) {
            console.error('Users Excel download failed:', err);
        }
    }

    public async downloadQuestionsAsExcel(): Promise<void> {
        if (this.data.type !== 'questions') {
            return;
        }

        try {
            await this.downloadQuestionsAsExcelInternal();
        } catch (err) {
            console.error('Questions Excel download failed:', err);
        }
    }

    public async downloadFeedbackAsExcel(): Promise<void> {
        if (this.data.type !== 'feedback') {
            return;
        }

        try {
            await this.downloadFeedbackAsExcelInternal();
        } catch (err) {
            console.error('Feedback Excel download failed:', err);
        }
    }

    public async downloadConsolidatedAsExcel(): Promise<void> {
        if (this.data.type !== 'consolidated') {
            return;
        }

        try {
            await this.downloadConsolidatedAsExcelInternal();
        } catch (err) {
            console.error('Consolidated report Excel download failed:', err);
        }
    }

    // ── Feedback: computed lists ────────────────────────────────────────────

    public get uniqueUserNames(): string[] {
        const names = (this.data.feedbackItems ?? [])
            .map(f => f.user_name)
            .filter(n => !!n?.trim());
        return [...new Set(names)].sort();
    }

    public get dropdownUserNames(): string[] {
        const term = this.dropdownSearch.trim().toLowerCase();
        if (!term) return this.uniqueUserNames;
        return this.uniqueUserNames.filter(n => n.toLowerCase().includes(term));
    }

    public get filteredItems(): FeedbackItemApi[] {
        const base = this.selectedUser
            ? (this.data.feedbackItems ?? []).filter(f => f.user_name === this.selectedUser)
            : (this.data.feedbackItems ?? []);
        
        // Filter by rating if selected
        let filtered = base;
        if (this.selectedRating !== null) {
            filtered = base.filter(f => {
                const rating = Number(f.feedback?.response_rating) || 0;
                return rating === this.selectedRating;
            });
        }
        
        // Sort by date descending (newest first)
        return [...filtered].sort((a, b) => {
            const dateA = a.feedback?.submitted_at ? new Date(a.feedback.submitted_at).getTime() : 0;
            const dateB = b.feedback?.submitted_at ? new Date(b.feedback.submitted_at).getTime() : 0;
            return dateB - dateA;
        });
    }

    public toggleDropdown(event: MouseEvent): void {
        event.stopPropagation();
        this.dropdownOpen = !this.dropdownOpen;
        if (this.dropdownOpen) {
            this.dropdownSearch = '';
            this.ratingDropdownOpen = false;
        }
    }

    public stopProp(event: MouseEvent): void { event.stopPropagation(); }

    public selectUser(name: string | null, event: MouseEvent): void {
        event.stopPropagation();
        this.selectedUser = name;
        this.dropdownOpen = false;
    }

    public toggleRatingDropdown(event: MouseEvent): void {
        event.stopPropagation();
        this.ratingDropdownOpen = !this.ratingDropdownOpen;
        if (this.ratingDropdownOpen) {
            this.dropdownOpen = false;
        }
    }

    public selectRating(rating: number | null, event: MouseEvent): void {
        event.stopPropagation();
        this.selectedRating = rating;
        this.ratingDropdownOpen = false;
    }

    /** Close dropdown when clicking anywhere outside the filter area */
    @HostListener('document:click')
    public onDocumentClick(): void {
        this.dropdownOpen = false;
        this.ratingDropdownOpen = false;
        this.questionDropdownOpen = false;
        this.categoryDropdownOpen = false;
        this.managerDropdownOpen = false;
    }

    // ── Questions: computed lists ───────────────────────────────────────

    public get uniqueQuestionUserNames(): string[] {
        const emails = new Set((this.data.questionItems ?? []).map(q => q.user_email));
        return Array.from(emails).sort();
    }

    public get uniqueCategories(): string[] {
        const categories = new Set((this.data.questionItems ?? []).map(q => q.agent_category));
        return Array.from(categories).sort();
    }

    public get questionDropdownUserNames(): string[] {
        const term = this.questionDropdownSearch.trim().toLowerCase();
        if (!term) return this.uniqueQuestionUserNames;
        return this.uniqueQuestionUserNames.filter(email => email.toLowerCase().includes(term));
    }

    public get categoryDropdownCategories(): string[] {
        const term = this.categoryDropdownSearch.trim().toLowerCase();
        if (!term) return this.uniqueCategories;
        return this.uniqueCategories.filter(cat => cat.toLowerCase().includes(term));
    }

    public get filteredQuestionItems(): UserAgentQuestionItemApi[] {
        let filtered = this.data.questionItems ?? [];

        // Filter by category (default to CES Search Agent)
        const category = this.selectedCategory || 'CES Search Agent';
        filtered = filtered.filter(q => q.agent_category === category);

        // Filter by user if selected
        if (this.selectedQuestionUser) {
            filtered = filtered.filter(q => q.user_email === this.selectedQuestionUser);
        }

        return filtered;
    }

    public toggleQuestionDropdown(event: MouseEvent): void {
        event.stopPropagation();
        this.questionDropdownOpen = !this.questionDropdownOpen;
        if (this.questionDropdownOpen) {
            this.questionDropdownSearch = '';
            this.categoryDropdownOpen = false;
        }
    }

    public toggleCategoryDropdown(event: MouseEvent): void {
        event.stopPropagation();
        this.categoryDropdownOpen = !this.categoryDropdownOpen;
        if (this.categoryDropdownOpen) {
            this.categoryDropdownSearch = '';
            this.questionDropdownOpen = false;
        }
    }

    public selectQuestionUser(email: string | null, event: MouseEvent): void {
        event.stopPropagation();
        this.selectedQuestionUser = email;
        this.questionDropdownOpen = false;
    }

    public selectCategory(category: string | null, event: MouseEvent): void {
        event.stopPropagation();
        this.selectedCategory = category || 'CES Search Agent';
        this.categoryDropdownOpen = false;
    }

    // ── Consolidated report: computed lists ──────────────────────────────

    public get sortedManagerStats(): ManagerStatApi[] {
        return [...(this.data.managerStats ?? [])].sort((left, right) => right.active_users - left.active_users);
    }

    public get pagedManagerStats(): ManagerStatApi[] {
        const start = this.consolidatedCurrentPage * this.pageSize;
        return this.sortedManagerStats.slice(start, start + this.pageSize);
    }

    public get consolidatedTotalCount(): number {
        return this.sortedManagerStats.length;
    }

    public get consolidatedTotalPages(): number {
        return Math.max(1, Math.ceil(this.consolidatedTotalCount / this.pageSize));
    }

    public get consolidatedRangeStart(): number {
        return this.consolidatedTotalCount === 0 ? 0 : this.consolidatedCurrentPage * this.pageSize + 1;
    }

    public get consolidatedRangeEnd(): number {
        return Math.min((this.consolidatedCurrentPage + 1) * this.pageSize, this.consolidatedTotalCount);
    }

    public prevConsolidatedPage(): void {
        if (this.consolidatedCurrentPage > 0) {
            this.consolidatedCurrentPage--;
        }
    }

    public nextConsolidatedPage(): void {
        if (this.consolidatedCurrentPage < this.consolidatedTotalPages - 1) {
            this.consolidatedCurrentPage++;
        }
    }

    // ── Shared helpers ──────────────────────────────────────────────────────

    public getInitials(name: string): string {
        const parts = (name ?? '').split(/[,\s]+/).filter(Boolean);
        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
        return (name ?? '').slice(0, 2).toUpperCase();
    }

    public formatDate(iso: string | undefined): string {
        if (!iso) return '';
        try { return this.dateFormatter.format(new Date(iso)); } catch { return ''; }
    }

    /** Returns an array of 5 booleans: true = filled star */
    public getRatingStars(rating: number | undefined): boolean[] {
        const r = Math.min(5, Math.max(0, Math.round(Number(rating) || 0)));
        return Array(5).fill(false).map((_, i) => i < r);
    }

    public close(): void { this.dialogRef.close(); }

    private async downloadUsersAsExcelInternal(): Promise<void> {
        const users = this.data.users ?? [];
        if (!users.length) {
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(DetailDialogComponent.USERS_EXPORT_SHEET_NAME);

        worksheet.columns = [
            { header: 'User Name', key: 'name', width: 34 },
            { header: 'User Mail', key: 'email', width: 44 },
            { header: 'Manager Name', key: 'managerName', width: 34 },
            { header: 'Manager Mail', key: 'managerEmail', width: 44 },
            { header: 'User ID', key: 'userId', width: 30 }
        ];

        users.forEach((user) => {
            worksheet.addRow({
                name: user.name || '',
                email: user.email || '',
                managerName: user.manager_name || '',
                managerEmail: user.manager_email || '',
                userId: user.user_id || ''
            });
        });

        const headerRow = worksheet.getRow(1);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, name: 'Calibri', size: 11 };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F62FE' } };
            cell.alignment = { vertical: 'middle', horizontal: 'left' };
        });

        worksheet.views = [{ state: 'frozen', ySplit: 1 }];
        worksheet.eachRow((row) => {
            row.height = 20;
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const timestamp = this.getExportTimestamp();
        const fileName = `${DetailDialogComponent.USERS_EXPORT_FILENAME_PREFIX}_${timestamp}.xlsx`;
        this.triggerFileDownload(buffer, fileName);
    }

    private async downloadQuestionsAsExcelInternal(): Promise<void> {
        const questionItems = this.data.questionItems ?? [];
        if (!questionItems.length) {
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(DetailDialogComponent.QUESTIONS_EXPORT_SHEET_NAME);

        worksheet.columns = [
            { header: 'User Name / Mail', key: 'user', width: 38 },
            { header: 'Agent', key: 'agent', width: 28 },
            { header: 'Question', key: 'question', width: 90 }
        ];

        questionItems.forEach((item) => {
            const normalizedName = (item.user_name || '').trim();
            const normalizedEmail = (item.user_email || '').trim();
            const userCellValue = normalizedName
                ? `${normalizedName}${normalizedEmail ? ` (${normalizedEmail})` : ''}`
                : normalizedEmail;

            worksheet.addRow({
                user: userCellValue,
                agent: item.agent_category || '',
                question: item.question || ''
            });
        });

        const headerRow = worksheet.getRow(1);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, name: 'Calibri', size: 11 };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F62FE' } };
            cell.alignment = { vertical: 'middle', horizontal: 'left' };
        });

        worksheet.views = [{ state: 'frozen', ySplit: 1 }];
        worksheet.eachRow((row, rowNumber) => {
            row.height = 20;
            if (rowNumber > 1) {
                row.getCell(3).alignment = { wrapText: true, vertical: 'top', horizontal: 'left' };
            }
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const timestamp = this.getExportTimestamp();
        const fileName = `${DetailDialogComponent.QUESTIONS_EXPORT_FILENAME_PREFIX}_${timestamp}.xlsx`;
        this.triggerFileDownload(buffer, fileName);
    }

    private async downloadFeedbackAsExcelInternal(): Promise<void> {
        const allFeedbackItems = this.data.allFeedbackItems ?? this.data.feedbackItems ?? [];
        if (!allFeedbackItems.length) {
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(DetailDialogComponent.FEEDBACK_EXPORT_SHEET_NAME);

        worksheet.columns = [
            { header: 'Date', key: 'date', width: 24 },
            { header: 'User Email', key: 'userEmail', width: 44 },
            { header: 'User Question', key: 'userQuestion', width: 68 },
            { header: 'Sentiment', key: 'sentiment', width: 16 },
            { header: 'Document Relevant', key: 'documentRelevant', width: 20 },
            { header: 'Answer Aligned', key: 'answerAligned', width: 18 },
            { header: 'Rating', key: 'rating', width: 12 },
            { header: 'Feedback', key: 'feedback', width: 68 }
        ];

        allFeedbackItems.forEach((item) => {
            worksheet.addRow(this.buildFeedbackExportRow(item));
        });

        const headerRow = worksheet.getRow(1);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, name: 'Calibri', size: 11 };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F62FE' } };
            cell.alignment = { vertical: 'middle', horizontal: 'left' };
        });

        worksheet.views = [{ state: 'frozen', ySplit: 1 }];
        worksheet.eachRow((row, rowNumber) => {
            row.height = rowNumber === 1 ? 20 : 22;
            if (rowNumber > 1) {
                row.getCell(3).alignment = { wrapText: true, vertical: 'top', horizontal: 'left' };
                row.getCell(8).alignment = { wrapText: true, vertical: 'top', horizontal: 'left' };
            }
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const timestamp = this.getExportTimestamp();
        const fileName = `${DetailDialogComponent.FEEDBACK_EXPORT_FILENAME_PREFIX}_${timestamp}.xlsx`;
        this.triggerFileDownload(buffer, fileName);
    }

    private async downloadConsolidatedAsExcelInternal(): Promise<void> {
        const managerStats = this.data.managerStats ?? [];
        if (!managerStats.length) {
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(DetailDialogComponent.CONSOLIDATED_EXPORT_SHEET_NAME);

        worksheet.columns = [
            { header: 'Manager Name', key: 'managerName', width: 36 },
            { header: 'No. of Users Have Access', key: 'totalUsers', width: 28 },
            { header: 'No. of Active Users', key: 'activeUsers', width: 24 },
            { header: 'Total Feedback Count', key: 'totalFeedbackCount', width: 24 }
        ];

        managerStats.forEach((item) => {
            worksheet.addRow({
                managerName: item.manager_name || '',
                totalUsers: Number(item.total_users_under_manager) || 0,
                activeUsers: Number(item.active_users) || 0,
                totalFeedbackCount: Number(item.total_feedback_count) || 0
            });
        });

        const headerRow = worksheet.getRow(1);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, name: 'Calibri', size: 11 };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F62FE' } };
            cell.alignment = { vertical: 'middle', horizontal: 'left' };
        });

        worksheet.views = [{ state: 'frozen', ySplit: 1 }];
        worksheet.eachRow((row) => {
            row.height = 20;
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const timestamp = this.getExportTimestamp();
        const fileName = `${DetailDialogComponent.CONSOLIDATED_EXPORT_FILENAME_PREFIX}_${timestamp}.xlsx`;
        this.triggerFileDownload(buffer, fileName);
    }

    private triggerFileDownload(buffer: BlobPart, fileName: string): void {
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
    }

    private getExportTimestamp(): string {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const hh = String(now.getHours()).padStart(2, '0');
        const min = String(now.getMinutes()).padStart(2, '0');
        const ss = String(now.getSeconds()).padStart(2, '0');
        return `${yyyy}${mm}${dd}_${hh}${min}${ss}`;
    }

    private formatFeedbackExportDate(iso: string | undefined): string {
        if (!iso) {
            return '';
        }

        const parsedDate = new Date(iso);
        if (Number.isNaN(parsedDate.getTime())) {
            return iso;
        }

        const yyyy = parsedDate.getFullYear();
        const mm = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const dd = String(parsedDate.getDate()).padStart(2, '0');
        const hh = String(parsedDate.getHours()).padStart(2, '0');
        const min = String(parsedDate.getMinutes()).padStart(2, '0');
        const ss = String(parsedDate.getSeconds()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
    }

    private buildFeedbackExportRow(item: FeedbackItemApi): Record<string, string | number> {
        const rating = Number(item.feedback?.response_rating);
        const sentiment = (item.feedback?.sentiment || '').trim().toLowerCase();

        return {
            date: this.formatFeedbackExportDate(item.feedback?.submitted_at),
            userEmail: (item.user_email || '').trim(),
            userQuestion: (item.question || '').trim(),
            sentiment: sentiment || 'null',
            documentRelevant: (item.feedback?.documents_relevant || '').trim() || '—',
            answerAligned: (item.feedback?.answer_aligned || '').trim() || '—',
            rating: Number.isFinite(rating) ? rating : '',
            feedback: (item.feedback?.additional_comments || '').trim(),
        };
    }
}
